/**
 * API Service Layer for Froth Desk Self Service Portal
 * Uses Axios for HTTP requests with proper error handling
 */

import axios from "axios";
import { encryptPassword } from "./crypto";

/**
 * Credential convention for this module:
 * - `loginUser(username, password)` takes the RAW password from the login
 *   form and encrypts it here before it leaves the browser.
 * - Every other exported function takes an ALREADY-ENCRYPTED password (the
 *   value stored in `localStorage.ssCreds` by the login flow) and forwards
 *   it as-is. Do not encrypt again in those functions.
 * The backend decrypts with the shared `CRYPTO_SECRET`.
 */

// Configuration
const API_CONFIG = {
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
};

// Create axios instance
const apiClient = axios.create(API_CONFIG);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't intercept errors that are already handled by the API functions
    if (error._isCustomError) {
      return Promise.reject(error);
    }

    // Handle different error types
    let errorMessage = "Something went wrong";

    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      // Check if the response has our custom error format
      if (data?.detail?.status === "false" || data?.detail?.status === false) {
        // Return the original error data for further processing
        return Promise.reject({
          ...error,
          _hasCustomError: true,
          _customError: data.detail.message || data.detail.error_type
        });
      }

      if (typeof data?.detail === "string" && data.detail) {
        // Some endpoints fail with the message as a plain string:
        // { detail: "TicketUpdate: Does not have permissions ...", request_id }
        // Prefer it over the generic status-code text.
        errorMessage = data.detail;
      } else {
        switch (status) {
          case 401:
            errorMessage =
              "Authentication failed. Please check your credentials and try again.";
            break;
          case 403:
            errorMessage =
              "Access denied. You do not have permission to perform this action.";
            break;
          case 404:
            errorMessage = "Resource not found.";
            break;
          case 500:
            errorMessage = "Server error. Please try again later.";
            break;
          default:
            errorMessage =
              data?.message || data?.error || `HTTP ${status}: Request failed`;
        }
      }
    } else if (error.request) {
      // Request made but no response received
      errorMessage =
        "Network error. Please check your connection and try again.";
    } else {
      // Error in request configuration
      errorMessage = error.message || "Unknown error occurred";
    }

    console.error("API Error:", errorMessage, error);

    // Return consistent error format
    return Promise.reject({
      success: false,
      message: errorMessage,
      error: error,
    });
  },
);

// Session management utility
const sessionService = {
  getSessionId: () => localStorage.getItem("sessionId"),
  setSessionId: (sessionId) => localStorage.setItem("sessionId", sessionId),
  clearSessionId: () => localStorage.removeItem("sessionId"),
  isLoggedIn: () => !!localStorage.getItem("sessionId"),
};

// Helper function to clean up assignedTo field
const cleanAssignedTo = (assignedTo) => {
  if (!assignedTo || assignedTo === "-") return "-";

  // Remove @ and everything after it
  const cleanedValue = assignedTo.split('@')[0];

  // Check if it's a system user or empty
  const systemUsers = ['root', 'localhost', 'admin', 'system', ''];
  if (systemUsers.includes(cleanedValue.toLowerCase().trim())) {
    return "-";
  }

  return cleanedValue || "-";
};

/**
 * Extract a human-readable message from a thrown API error, always returning
 * a string (safe to hand to toast.error / render). Thrown errors arrive in
 * several shapes:
 * - API-function custom throws: `{ success, error: "<string>" }`
 * - Interceptor rejections: `{ success, message: "<string>", error: <AxiosError> }`
 *   (here `error.error` is an object — rendering it directly breaks the toast)
 * - Raw AxiosError
 * @param {Object|string} error
 * @param {string} fallback
 * @returns {string}
 */
export const getErrorMessage = (error, fallback = "Something went wrong") => {
  if (typeof error === "string" && error) return error;
  const candidates = [
    error?.error,
    error?._customError,
    error?.message,
    error?.response?.data?.detail,
    error?.response?.data?.message,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate) return candidate;
  }
  return fallback;
};

/**
 * Login user and create session
 * @param {string} username - Username/email
 * @param {string} password - Password
 * @returns {Promise} API response
 */
export const loginUser = async (username, password) => {
  try {
    // Login using /api/ticket/login endpoint
    const response = await apiClient.post(`/api/login/`, {
      username: username,
      password: encryptPassword(password),
    });

    const data = response.data;

    // Handle various error response formats - even if status is 200
    const isErrorResponse =
      (data?.detail?.status === "false" || data?.detail?.status === false) ||
      (data?.status === false || data?.status === "false") ||
      (data?.error) ||
      (data?.detail?.error) ||
      (data?.detail?.status) ||
      (data?.detail?.message);

    if (isErrorResponse) {
      // Extract error message from various possible locations
      const errorMessage =
        data?.detail?.message ||
        data?.detail?.error_type ||
        data?.detail?.error ||
        data?.message ||
        data?.error ||
        "Invalid credentials";

      throw {
        success: false,
        error: errorMessage,
        _isCustomError: true,
      };
    }

    // Handle success response format
    if (data?.status === true || data?.status === "true") {
      const token = data.session_id;

      // Store token
      sessionService.setSessionId(token);

      // Return user data
      return {
        success: true,
        data: {
          token: token,
          session_id: token,
          user: {
            id: 1,
            email: username,
            name: username,
            avatar:
              "https://ui-avatars.com/api/?name=" +
              encodeURIComponent(username.split("@")[0]) +
              "&background=0066cc&color=fff",
          },
        },
        message: data.message || "Login successful",
      };
    }

    // Extract token from response - handle multiple possible formats
    let token;

    // Check different possible token locations
    if (data.data && data.data.token) {
      token = data.data.token;
    } else if (data.data && data.data.session_id) {
      token = data.data.session_id;
    } else if (data.token) {
      token = data.token;
    } else if (data.session_id) {
      token = data.session_id;
    } else {
      throw new Error(data.error || "Something went wrong");
    }

    // Store token
    sessionService.setSessionId(token);

    // Return user data
    const responseData = {
      success: true,
      data: {
        token: token,
        session_id: token, // Always include session_id
        user: {
          id: 1,
          email: username,
          name: username,
          avatar:
            "https://ui-avatars.com/api/?name=" +
            encodeURIComponent(username.split("@")[0]) +
            "&background=0066cc&color=fff",
        },
      },
      message: data.message || "Login successful",
    };

    return responseData;
  } catch (error) {
    // Handle custom error format from interceptor
    if (error._hasCustomError) {
      throw {
        success: false,
        error: error._customError,
        _isCustomError: true,
      };
    }

    // Check for 401 status specifically to return the actual error message
    if (error.response && error.response.status === 401) {
      const data = error.response.data;
      if (data?.detail?.message) {
        throw {
          success: false,
          error: data.detail.message,
          _isCustomError: true,
        };
      }
    }

    // Normalize all errors to one format
    const message = error?.error || error?.response?.data?.detail?.message || error?.response?.data?.message || error?.message || "Login failed";
    throw {
      success: false,
      error: message,
    };
  }
};

/**
 * Create a new ticket
 * @param {string} username - Username
 * @param {string} password - Password
 * @param {Object} ticketData - Ticket data
 * @returns {Promise} API response
 */
export const createTicket = async (
  username,
  password,
  ticketData
) => {
  try {
    const payload = {
      username,
      password,
      Ticket: ticketData.Ticket,
      Article: ticketData.Article,
      Attachment: ticketData.Attachment || []
    };

    const response = await apiClient.post(`/api/TicketCreate`, payload);

    return {
      success: true,
      data: {
        id: response.data.data?.id || Date.now(),
        ticketId: response.data.data?.id || Date.now(),
        caseNumber: response.data.data?.id || Date.now(),
        title: payload.Ticket.Title,
        ticketType: payload.Ticket.Type,
        status: payload.Ticket.State,
        severity: payload.Ticket.Priority,
        createdBy: payload.Ticket.CustomerUser,
        createdOn: new Date().toISOString(),
        description: payload.Article.Body,
        resolution: "",
        Attachment: payload.Attachment
      },
      message: "Ticket created successfully",
    };

  } catch (error) {
    if (error.response?.status === 401) {
      throw {
        success: false,
        error: "Invalid credentials",
      };
    }

    throw error;
  }
};

/**
 * Get ticket by ID
 * @param {string} username - Username
 * @param {string} password - Password
 * @param {string} ticketId - Ticket ID
 * @returns {Promise} API response
 */
export const getTicket = async (username, password, ticketId) => {
  try {
    const response = await apiClient.get(`/api/TicketGet/${ticketId}`, {
      params: {
        username,
        password,
      },
    });

    const ticketData = response.data;

    // Actual ticket object
    const ticket = ticketData.Ticket?.[0] || {};

    return {
      success: true,
      data: {
        id: ticket.TicketID || ticketId,
        ticketId: ticket.TicketID || ticketId,
        caseNumber: ticket.TicketNumber || "-",
        title: ticket.Title || "-",
        ticketType: ticket.Type || "-",
        status: ticket.State || "-",
        severity: ticket.Priority || "-",
        createdBy: ticket.CustomerUserID || ticket.CustomerID || "-",
        createdOn: ticket.Created || new Date().toISOString(),
        description: ticket.Article?.[0]?.Body || "",
        resolution:
          ticket.Article?.find((a) =>
            a.Subject?.toLowerCase().includes("resolved"),
          )?.Body || "",
        assignedTo: cleanAssignedTo(ticket.Owner || "-"),
        attachments:
          ticket.Article?.flatMap((article) => article.Attachment || []) || [],
        comments: ticket.Article || [],
        SLA: ticket.SLA || "-",
      },
      message: "Ticket retrieved successfully",
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get all tickets for the user
 * @param {string} username - Username
 * @param {string} password - Password
 * @returns {Promise} API response
 */
export const getAllTickets = async (username, password) => {
  try {
    // Prepare query parameters with username and password
    const params = {
      username: username,
      password: password,
    };

    const response = await apiClient.get("/api/TicketSearch", {
      params,
    });

    const responseData = response.data;

    // Transform API response to match expected format
    const tickets =
      responseData.tickets?.map((ticket) => ({
        id: ticket.TicketID,
        ticketId: ticket.TicketID,
        caseNumber: ticket.TicketNumber,
        assignedTo: cleanAssignedTo(ticket.Owner),
        title: ticket.Title,
        ticketType: ticket.Type,
        status: ticket.State,
        severity: ticket.Priority,

        createdBy: ticket.CustomerUserID,
        createdOn: ticket.Created,
        customer_id: ticket.CustomerID, // Store customer_id for company tickets API

        description: ticket.Article?.[0]?.Body || "",
        resolution:
          ticket.Article?.find((a) =>
            a.Subject?.toLowerCase().includes("resolved"),
          )?.Body || "",

        attachments: [],

        comments: ticket.Article || [],
      })) || [];

    return {
      success: true,
      data: {
        tickets,
      },
      message: "Tickets retrieved successfully",
    };
  } catch (error) {
    // Handle authentication errors specifically
    if (error.response && error.response.status === 401) {
      throw {
        success: false,
        error: "Invalid credentials",
      };
    }
    // For other errors, use the interceptor's format
    throw error;
  }
};

/**
 * Get all company tickets (tickets raised across the customer's organization)
 * @param {string} username - Username
 * @param {string} password - Password
 * @param {string} customer_id - Customer ID extracted from TicketSearch response
 * @returns {Promise} API response
 */
export const getCompanyTickets = async (username, password, customer_id) => {
  try {
    const params = {
      username: username,
      password: password,
      customer_id: customer_id,
    };

    const response = await apiClient.get("/api/TicketSearchCompany", {
      params,
    });

    const responseData = response.data;

    const tickets =
      responseData.tickets
        ?.filter((ticket) => ticket.TicketNumber)
        .map((ticket) => ({
        id: ticket.TicketID,
        ticketId: ticket.TicketID,
        caseNumber: ticket.TicketNumber,
        assignedTo: cleanAssignedTo(ticket.Owner),
        title: ticket.Title,
        ticketType: ticket.Type,
        status: ticket.State,
        severity: ticket.Priority,

        createdBy: ticket.CustomerUserID,
        createdOn: ticket.Created,

        description: ticket.Article?.[0]?.Body || "",
        resolution:
          ticket.Article?.find((a) =>
            a.Subject?.toLowerCase().includes("resolved"),
          )?.Body || "",

        attachments: [],

        comments: ticket.Article || [],
      })) || [];

    return {
      success: true,
      data: {
        tickets,
      },
      message: "Company tickets retrieved successfully",
    };
  } catch (error) {
    if (error.response && error.response.status === 401) {
      throw {
        success: false,
        error: "Invalid credentials",
      };
    }
    throw error;
  }
};

/**
 * Update ticket
 * @param {string} username - Username
 * @param {string} password - Password
 * @param {string} ticketId - Ticket ID
 * @param {Object} data - Update data
 * @returns {Promise} API response
 */
export const updateTicket = async (username, password, ticketId, data) => {
  try {
    // Prepare payload according to API requirements
    const updateData = {
      username: username,
      password: password,
      state: data?.Ticket?.State || data?.state,
      comment: data?.Article?.Body || data?.comment,
      Ticket: data?.Ticket,
      Article: data?.Article,
      Attachment: data?.Article?.Attachment || []
    };

    const response = await apiClient.put(`/api/TicketUpdate/${ticketId}`, updateData);
    return {
      success: true,
      data: {
        id: ticketId,
        ticketId: ticketId,
        caseNumber: ticketId,
        title: data.Ticket?.Title || "Ticket",
        status: data.Ticket?.State || "Updated",
        updatedOn: new Date().toISOString(),
        message: "Ticket updated successfully",
      },
      message: "Ticket updated successfully",
    };
  } catch (error) {
    // Error is already formatted by the interceptor
    throw error;
  }
};

// Export API client for custom requests if needed
export { apiClient };

// Export session service
export { sessionService };

// Make session service available globally
if (typeof window !== "undefined") {
  window.sessionService = sessionService;
}

export default {
  loginUser,
  createTicket,
  getTicket,
  updateTicket,
  getAllTickets,
  getCompanyTickets,
  sessionService,
};
